import React from "react";
import { Badge, Box, HStack, VStack, Text } from "@chakra-ui/react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { STATUS_COLORS } from "./statusColors";
import {
  chipText, effectOf, impactColor, impactSlices, notSized, pctOf, summarizeImpact, MINOR_ISSUE, RISK, STATE_SCHEME,
  STATE_WORDS, URGENT_ISSUE,
} from "./impact";

export { STATUS_COLORS };

function percent(count, total) {
  return total ? Math.round((count / total) * 100) : 0;
}

function SliceTooltip({ active, payload, total }) {
  const slice = active && payload?.[0]?.payload;
  if (!slice) return null;
  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" px={2} py={1} fontSize="xs" boxShadow="sm"
         whiteSpace="nowrap">
      {slice.count} {slice.label} · {percent(slice.count, total)}%
    </Box>
  );
}

/**
 * The overall split of a set of results as a small donut (e.g. 90% green, 10% yellow): the share
 * of the first slice (the good one) in the middle, and a legend with every count and its share in
 * words. Slices are given in a fixed order and keep their colours whatever the counts.
 */
export default function StatusDonut({ slices, centerLabel, testId, size = 104 }) {
  const shown = slices.filter((s) => s.count > 0);
  const total = shown.reduce((sum, s) => sum + s.count, 0);
  if (!total) return null;
  const words = shown.map((s) => `${s.count} ${s.label}`).join(", ");
  return (
    <HStack spacing={4} align="center" data-testid={testId} role="img" aria-label={words} flexShrink={0}>
      <Box position="relative" w={`${size}px`} h={`${size}px`} flexShrink={0}>
        <PieChart width={size} height={size}>
          {/* The white stroke is the gap between slices. */}
          <Pie data={shown} dataKey="count" nameKey="label" innerRadius={size * 0.32} outerRadius={size * 0.48}
               startAngle={90} endAngle={-270} stroke="#ffffff" strokeWidth={2} isAnimationActive={false}>
            {shown.map((s) => <Cell key={s.key} fill={s.color} />)}
          </Pie>
          <Tooltip content={<SliceTooltip total={total} />} allowEscapeViewBox={{ x: true, y: true }}
                   wrapperStyle={{ zIndex: 10 }} />
        </PieChart>
        <VStack position="absolute" inset={0} spacing={0} justify="center" pointerEvents="none"
                data-testid={`${testId}-center`}>
          <Text fontSize="lg" fontWeight="bold" lineHeight="1">{percent(slices[0].count, total)}%</Text>
          <Text fontSize="2xs" color="gray.600">{centerLabel}</Text>
        </VStack>
      </Box>
      <VStack align="start" spacing={0.5} data-testid={`${testId}-legend`}>
        {shown.map((s) => (
          <HStack key={s.key} spacing={2}>
            <Box w="10px" h="10px" borderRadius="sm" bg={s.color} flexShrink={0} />
            <Text fontSize="sm">
              {s.count} {s.label} <Text as="span" color="gray.500">({percent(s.count, total)}%)</Text>
            </Text>
          </HStack>
        ))}
      </VStack>
    </HStack>
  );
}

// --- how bad each issue is (impact.js) ------------------------------------------------------

/** A tab's health as a whole number, except just under 100, where rounding would hide open issues. */
export function healthText(health) {
  if (health >= 100) return "100";
  if (health >= 99) return String(Math.floor(health * 10) / 10);
  return String(Math.round(health));
}

/** A combined share in whole percent ("<1" below one). */
export function pctText(pct) {
  return pct > 0 && pct < 1 ? "<1" : String(Math.round(pct));
}

function sizeScheme(value) {
  const pct = pctOf(value);
  if (!value || value.not_estimated || pct === null) return "gray";
  if (pct >= URGENT_ISSUE) return "red";
  if (pct >= MINOR_ISSUE) return "orange";
  return pct > 0 ? "yellow" : "green";
}

/** One issue's impact as a small badge: "≈30% of requests", "<2% of users", "impact not estimated". */
export function ImpactChip({ impact, testId }) {
  return (
    <Badge colorScheme={sizeScheme(impact)} variant="subtle" fontSize="2xs" textTransform="none"
           data-testid={testId} title={impact?.basis || undefined}>
      {chipText(impact)}
    </Badge>
  );
}

/** The grouped smaller issues' size: none bigger than the biggest of them. */
export function eachText(pct) {
  if (pct < 1) return "each under 1%";
  if (pct < MINOR_ISSUE) return "each under 2%";
  return `each ≈${pctText(pct)}% or less`;
}

function ImpactTooltip({ active, payload }) {
  const slice = active && payload?.[0]?.payload;
  if (!slice || slice.key === "pending") return null;
  const what = slice.key === "healthy" ? `${healthText(slice.pct)}% untouched`
    : slice.key === "now" ? `${pctText(slice.pct)}%`
      : slice.impact ? chipText(slice.impact) : eachText(slice.pct);
  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" px={2} py={1} fontSize="xs" boxShadow="sm"
         maxW="260px">
      {slice.label} · {what}
    </Box>
  );
}

/**
 * How bad a tab's issues are, as a donut: each open issue is a slice sized by its impact and
 * coloured by its own size (red from 25%, orange from 2%, yellow below), the rest of the ring is
 * green (the tab's health), the five biggest are named in the legend and the rest grouped. Items
 * are {key, label, impact, pending?}: impact null means couldn't be checked, or not checked yet
 * when pending; an impact marked not_estimated is a finding not sized yet. Those three are listed
 * under the ring, outside the health number. So a pile of small issues reads as a mostly green
 * ring, not a red one.
 */
export function ImpactDonut({ items, testId, label = "Issues", size = 104, top = 5, memory = false, usedPct = null,
                             startPct = null, nothingText = "Nothing open" }) {
  const all = items || [];
  const knownItems = all.filter((i) => pctOf(i) !== null);
  const pending = all.filter((i) => pctOf(i) === null && i.pending).length;
  const unsized = all.filter((i) => pctOf(i) === null && !i.pending && notSized(i)).length;
  const unknown = all.length - knownItems.length - pending - unsized;
  const options = { memory, usedPct, startPct };
  const { health, state, serious_risks: serious } = summarizeImpact(knownItems, top, options);
  const scheme = STATE_SCHEME[state];
  const checked = knownItems.length > 0;
  const slices = checked ? impactSlices(knownItems, { top, ...options })
    : [{ key: "pending", label: "not checked yet", value: 1, color: STATUS_COLORS.pending }];
  const drawn = slices.filter((s) => s.value > 0);
  const named = slices.filter((s) => !["healthy", "rest", "pending", "now"].includes(s.key));
  const rest = slices.find((s) => s.key === "rest");
  // Working normally, but with risks that need fixing now: say both, not a low health.
  const riskOnly = state === "urgent" && health > 100 - URGENT_ISSUE && serious > 0;
  const heading = !checked ? "Not checked yet"
    : riskOnly ? `Working normally · ${serious} serious ${serious === 1 ? "risk" : "risks"} to fix now`
      : `Health ${healthText(health)}% · ${STATE_WORDS[state]}`;
  const words = [
    `${label}: ${checked ? `health ${healthText(health)}%, ${STATE_WORDS[state]}` : "not checked yet"}`,
    ...named.map((s) => `${s.label} ${chipText(s.impact)}`),
    rest && `${rest.label}, ${eachText(rest.pct)}`,
  ].filter(Boolean).join("; ");
  return (
    <VStack align="start" spacing={2} data-testid={testId} data-state={checked ? state : "unchecked"} flexShrink={0}>
      <Text fontSize="sm" fontWeight="bold" color={checked ? `${scheme}.600` : "gray.600"}
            data-testid={`${testId}-heading`}>
        {heading}
      </Text>
      <HStack spacing={4} align="center" role="img" aria-label={words}>
        <Box position="relative" w={`${size}px`} h={`${size}px`} flexShrink={0}>
          <PieChart width={size} height={size}>
            <Pie data={drawn} dataKey="value" nameKey="label" innerRadius={size * 0.32} outerRadius={size * 0.48}
                 startAngle={90} endAngle={-270} stroke="#ffffff" strokeWidth={2} isAnimationActive={false}>
              {drawn.map((s) => <Cell key={s.key} fill={s.color} />)}
            </Pie>
            <Tooltip content={<ImpactTooltip />} allowEscapeViewBox={{ x: true, y: true }} wrapperStyle={{ zIndex: 10 }} />
          </PieChart>
          <VStack position="absolute" inset={0} spacing={0} justify="center" pointerEvents="none"
                  data-testid={`${testId}-center`}>
            <Text fontSize="lg" fontWeight="bold" lineHeight="1" color={checked ? `${scheme}.600` : "gray.500"}>
              {checked ? `${healthText(health)}%` : "—"}
            </Text>
            <Text fontSize="2xs" color="gray.600">{checked ? "health" : "not checked yet"}</Text>
          </VStack>
        </Box>
        <VStack align="start" spacing={0.5} data-testid={`${testId}-legend`} minW={0}>
          {named.map((s) => (
            <HStack key={s.key} spacing={2} align="baseline">
              <Box w="10px" h="10px" borderRadius="sm" bg={s.color} flexShrink={0} />
              <Text fontSize="sm" noOfLines={1} title={s.label}>
                {s.label} <Text as="span" color="gray.500">{chipText(s.impact)}</Text>
              </Text>
            </HStack>
          ))}
          {rest && (
            <HStack spacing={2} align="baseline">
              <Box w="10px" h="10px" borderRadius="sm" bg={rest.color} flexShrink={0} />
              <Text fontSize="sm">
                {rest.label} <Text as="span" color="gray.500">— {eachText(rest.pct)}</Text>
              </Text>
            </HStack>
          )}
          {checked && !named.length && <Text fontSize="sm" color="green.700">{nothingText}</Text>}
        </VStack>
      </HStack>
      {(unknown > 0 || pending > 0 || unsized > 0) && (
        <Text fontSize="xs" color="gray.500" data-testid={`${testId}-unknown`}>
          {[unknown > 0 && `${unknown} couldn't be checked`, pending > 0 && `${pending} not checked yet`,
            unsized > 0 && `${unsized} not sized yet (the next nightly run sizes ${unsized === 1 ? "it" : "them"})`]
            .filter(Boolean).join(" · ")}
        </Text>
      )}
    </VStack>
  );
}


/**
 * What could go wrong, as a donut beside the health one (the Code Review tab): the biggest risk at its
 * own size, coloured by it (red from 25%, orange from 2%, yellow below), the rest of the ring green
 * (not at risk), the five biggest risks named with their size and the rest grouped. Items are
 * {key, label, impact}; only risks are counted (an impact with effect "risk"), and findings not sized
 * yet are noted under it. Health says what's broken now; this says what to fix before it is.
 */
export function RiskDonut({ items, testId, label = "Risk", size = 104, top = 5 }) {
  const all = items || [];
  const risks = all
    .filter((i) => pctOf(i) !== null && pctOf(i) > 0 && effectOf(i) === RISK)
    .sort((a, b) => pctOf(b) - pctOf(a));
  const unsized = all.filter((i) => pctOf(i) === null && notSized(i)).length;
  const biggest = risks.length ? pctOf(risks[0]) : 0;
  const serious = risks.filter((i) => pctOf(i) >= URGENT_ISSUE).length;
  const named = risks.slice(0, top);
  const rest = risks.slice(top);
  const drawn = biggest > 0
    ? [{ key: "biggest", value: biggest, color: impactColor(biggest) },
       { key: "safe", value: 100 - biggest, color: STATUS_COLORS.good }]
    : [{ key: "safe", value: 100, color: STATUS_COLORS.good }];
  const scheme = biggest >= URGENT_ISSUE ? "red" : biggest >= MINOR_ISSUE ? "orange" : "green";
  const heading = !risks.length ? "Nothing at risk"
    : serious ? `${serious} serious ${serious === 1 ? "risk" : "risks"} to fix now`
      : `${risks.length} ${risks.length === 1 ? "risk" : "risks"}, none serious`;
  const words = [`${label}: ${heading}`, ...named.map((i) => `${i.label} ${chipText(i.impact)}`),
                 rest.length && `${rest.length} smaller, ${eachText(pctOf(rest[0]))}`].filter(Boolean).join("; ");
  return (
    <VStack align="start" spacing={2} data-testid={testId} flexShrink={0}>
      <Text fontSize="sm" fontWeight="bold" color={`${scheme}.600`} data-testid={`${testId}-heading`}>{heading}</Text>
      <HStack spacing={4} align="center" role="img" aria-label={words}>
        <Box position="relative" w={`${size}px`} h={`${size}px`} flexShrink={0}>
          <PieChart width={size} height={size}>
            <Pie data={drawn} dataKey="value" innerRadius={size * 0.32} outerRadius={size * 0.48} startAngle={90}
                 endAngle={-270} stroke="#ffffff" strokeWidth={2} isAnimationActive={false}>
              {drawn.map((s) => <Cell key={s.key} fill={s.color} />)}
            </Pie>
          </PieChart>
          <VStack position="absolute" inset={0} spacing={0} justify="center" pointerEvents="none"
                  data-testid={`${testId}-center`}>
            <Text fontSize="lg" fontWeight="bold" lineHeight="1" color={`${scheme}.600`}>
              {biggest > 0 ? `${pctText(biggest)}%` : "0%"}
            </Text>
            <Text fontSize="2xs" color="gray.600">at risk</Text>
          </VStack>
        </Box>
        <VStack align="start" spacing={0.5} data-testid={`${testId}-legend`} minW={0}>
          {named.map((i) => (
            <HStack key={i.key} spacing={2} align="baseline">
              <Box w="10px" h="10px" borderRadius="sm" bg={impactColor(pctOf(i))} flexShrink={0} />
              <Text fontSize="sm" noOfLines={1} title={i.label}>
                {i.label} <Text as="span" color="gray.500">{chipText(i.impact)}</Text>
              </Text>
            </HStack>
          ))}
          {rest.length > 0 && (
            <HStack spacing={2} align="baseline">
              <Box w="10px" h="10px" borderRadius="sm" bg={impactColor(pctOf(rest[0]))} flexShrink={0} />
              <Text fontSize="sm">
                {rest.length} smaller {rest.length === 1 ? "risk" : "risks"}{" "}
                <Text as="span" color="gray.500">— {eachText(pctOf(rest[0]))}</Text>
              </Text>
            </HStack>
          )}
          {!risks.length && <Text fontSize="sm" color="green.700">Nothing open that could go wrong</Text>}
        </VStack>
      </HStack>
      {unsized > 0 && (
        <Text fontSize="xs" color="gray.500" data-testid={`${testId}-unknown`}>
          {unsized} not sized yet (the next nightly run sizes {unsized === 1 ? "it" : "them"})
        </Text>
      )}
    </VStack>
  );
}
